import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Trash2, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Empty, Loading, PageHead, useAdmin } from "./ui";
import { HolidayRow, PlaylistEditor, PRESETS, count, fmtMd, presetDates } from "./MusicSchedule";

const DAY_LABEL = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
const API = process.env.REACT_APP_BACKEND_URL;

const UploadBox = ({ onDone }) => {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    setBusy(true);
    try {
      await api.post("/admin/music/upload", fd);
      toast.success("Track uploaded");
      setTitle("");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="ad-card" data-testid="music-upload">
      <h3>Upload music</h3>
      <p className="muted-sm">MP3, M4A, AAC, OGG, WAV or WEBM up to 25 MB. Only upload music you have the rights to play publicly.</p>
      <div className="mu-upload">
        <input placeholder="Track title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="music-title-input" />
        <label className={`btn gold ${busy ? "is-busy" : ""}`}>
          {busy ? <Loader2 size={15} className="spin" /> : <Upload size={15} />} {busy ? "Uploading…" : "Choose audio file"}
          <input type="file" accept="audio/*" hidden disabled={busy} onChange={upload} data-testid="music-file-input" />
        </label>
      </div>
    </div>
  );
};

const Library = ({ tracks, onChange }) => {
  const rename = async (t, title) => {
    if (!title.trim() || title === t.title) return;
    try { await api.patch(`/admin/music/tracks/${t.id}`, { title }); toast.success("Renamed"); onChange(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"? It will be removed from every day.`)) return;
    try { await api.delete(`/admin/music/tracks/${t.id}`); toast.success("Track deleted"); onChange(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="ad-card" data-testid="music-library">
      <h3>Music library</h3>
      {!tracks.length ? <Empty>No tracks yet — upload your first one above.</Empty> : tracks.map((t) => (
        <div className="mu-track" key={t.id} data-testid={`music-track-${t.id}`}>
          <input defaultValue={t.title} onBlur={(e) => rename(t, e.target.value)} aria-label="Track title" data-testid="music-track-title-input" />
          <audio controls preload="none" src={`${API}${t.url}`} data-testid="music-track-preview" />
          <button className="ad-icon-btn" onClick={() => remove(t)} aria-label="Delete track" data-testid="music-track-delete"><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
  );
};

const EMPTY_PL = { all: [], morning: [], afternoon: [], evening: [] };
const HOURS = Array.from({ length: 23 }, (_, i) => i + 1);
const hourLabel = (h) => `${((h - 1) % 12) + 1} ${h < 12 ? "AM" : "PM"}`;

const TimeOfDay = ({ s, setS }) => (
  <div className="ad-card" data-testid="music-periods">
    <h3>Time of day</h3>
    <p className="muted-sm">Every playlist below can have different tracks for the morning, afternoon and evening — for example calm tracks in the morning and upbeat ones in the evening.</p>
    <div className="mu-settings">
      <label className="mu-field">Morning ends at
        <select value={s.morning_end} onChange={(e) => setS({ ...s, morning_end: Number(e.target.value) })} data-testid="music-morning-end-select">
          {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
        </select>
      </label>
      <label className="mu-field">Evening starts at
        <select value={s.evening_start} onChange={(e) => setS({ ...s, evening_start: Number(e.target.value) })} data-testid="music-evening-start-select">
          {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
        </select>
      </label>
    </div>
  </div>
);

const Holidays = ({ s, setS, tracks }) => {
  const [preset, setPreset] = useState("");
  const add = () => {
    const [name, a, b] = PRESETS.find((p) => p[0] === preset) || ["", "12-25", ""];
    const [start, end] = presetDates(a, b);
    setS({ ...s, holidays: [...s.holidays, { id: `h${Date.now()}`, name, start, end, playlist: EMPTY_PL }] });
    setPreset("");
  };
  const update = (i, h) => setS({ ...s, holidays: s.holidays.map((x, j) => (j === i ? h : x)) });
  return (
    <div className="ad-card" data-testid="music-holidays">
      <h3>Special dates &amp; holidays</h3>
      <p className="muted-sm">These override the weekly schedule on the exact dates you pick (they don’t repeat — add them again next year). Set one day, or a range like Dec 20 – Dec 26, 2026.</p>
      <div className="mu-add">
        <select value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="music-holiday-preset-select">
          <option value="">Custom date…</option>
          {PRESETS.map(([n, a, b]) => <option key={n} value={n}>{`${n} (${fmtMd(a)}${b ? ` – ${fmtMd(b)}` : ""})`}</option>)}
        </select>
        <button type="button" className="btn light" onClick={add} data-testid="music-holiday-add-button">Add special date</button>
      </div>
      {s.holidays.map((h, i) => (
        <HolidayRow key={h.id} h={h} tracks={tracks} onChange={(v) => update(i, v)} onRemove={() => setS({ ...s, holidays: s.holidays.filter((_, j) => j !== i) })} />
      ))}
    </div>
  );
};

const Weekly = ({ s, setS, data }) => (
  <div className="ad-card" data-testid="music-schedule">
    <h3>Weekly schedule</h3>
    <p className="muted-sm">Tap tracks to add them to a playlist — numbers show the play order. Days left empty use the “Any other day” playlist.</p>
    {data.days.map((d) => (
      <details className={`mu-day ${d === data.today ? "is-today" : ""}`} key={d} open={d === data.today} data-testid={`music-day-${d}`}>
        <summary className="mu-day-name">{DAY_LABEL[d]}{d === data.today && <span className="pill">Today</span>} <span className="muted-sm">{count(s.schedule[d])} track{count(s.schedule[d]) === 1 ? "" : "s"}</span></summary>
        <PlaylistEditor id={d} tracks={data.tracks} value={s.schedule[d]} onChange={(v) => setS({ ...s, schedule: { ...s.schedule, [d]: v } })} />
      </details>
    ))}
    <details className="mu-day" open data-testid="music-day-everyday">
      <summary className="mu-day-name">Any other day <span className="muted-sm">{count(s.everyday)} track{count(s.everyday) === 1 ? "" : "s"}</span></summary>
      <PlaylistEditor id="everyday" tracks={data.tracks} value={s.everyday} onChange={(v) => setS({ ...s, everyday: v })} />
    </details>
  </div>
);

export default function Music() {
  const { data, refetch } = useAdmin("music", "/admin/music");
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  useEffect(() => { if (data) setS(data.settings); }, [data]);
  if (!data || !s) return <Loading />;
  const reload = () => { refetch(); qc.invalidateQueries({ queryKey: ["music"] }); };

  const save = async () => {
    setBusy(true);
    try {
      const { data: saved } = await api.put("/admin/music/settings", s);
      setS(saved);
      refetch();
      qc.invalidateQueries({ queryKey: ["music"] });
      toast.success("Music schedule saved");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-music">
      <PageHead eyebrow="Atmosphere" title="Background music">
        <button className="btn gold" onClick={save} disabled={busy} data-testid="music-save-button">{busy ? <Loader2 size={15} className="spin" /> : "Save schedule"}</button>
      </PageHead>
      <div className="ad-card" data-testid="music-settings">
        <h3>Player</h3>
        <p className="muted-sm">Music starts automatically for visitors (on phones and some browsers it begins the moment they first tap the page, because browsers block sound until then). Visitors can stop or restart it from the player, and it keeps playing as they browse.</p>
        <div className="mu-settings">
          <label className="ad-check"><input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} data-testid="music-enabled-checkbox" /> Music on the website</label>
          <label className="ad-check"><input type="checkbox" checked={s.shuffle} onChange={(e) => setS({ ...s, shuffle: e.target.checked })} data-testid="music-shuffle-checkbox" /> Shuffle</label>
          <label className="mu-field">Button label<input value={s.label} maxLength={40} onChange={(e) => setS({ ...s, label: e.target.value })} data-testid="music-label-input" /></label>
          <label className="mu-field">Volume {Math.round(s.volume * 100)}%
            <input type="range" min="0" max="1" step="0.05" value={s.volume} onChange={(e) => setS({ ...s, volume: Number(e.target.value) })} data-testid="music-volume-input" />
          </label>
        </div>
      </div>
      <UploadBox onDone={reload} />
      <Library tracks={data.tracks} onChange={reload} />
      {data.now.count > 0 && (
        <p className="mu-now" data-testid="music-now-playing">Playing on the site right now: <strong>{data.now.source}</strong> · {data.now.period} ({data.now.count} tracks)</p>
      )}
      {!data.tracks.length ? <div className="ad-card"><Empty>Upload music to build your schedule.</Empty></div> : (
        <>
          <TimeOfDay s={s} setS={setS} />
          <Holidays s={s} setS={setS} tracks={data.tracks} />
          <Weekly s={s} setS={setS} data={data} />
        </>
      )}
    </div>
  );
}
